import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle2, Eye, EyeOff, KeyRound, Loader2, Lock, Mail } from 'lucide-react';
import AuthFrame from '../../components/layout/AuthFrame';
import {
  confirmPasswordReset,
  MIN_PASSWORD_LENGTH,
  PASSWORD_POLICY_HINT,
  requestPasswordReset,
} from '../../lib/cognito';

// request  -> the address is entered and Cognito emails a code
// reset    -> the code plus the new password are submitted
// done     -> the password is changed; the user goes back to sign in
type Step = 'request' | 'reset' | 'done';

export default function ForgotPassword() {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const sendCode = async (): Promise<boolean> => {
    const { error: requestError, destination: sentTo } = await requestPasswordReset(email);
    if (requestError) {
      setError(requestError);
      return false;
    }
    setDestination(sentTo ?? '');
    return true;
  };

  const handleRequest = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    if (await sendCode()) setStep('reset');
    setLoading(false);
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    setLoading(true);
    if (await sendCode()) setNotice('We have sent a new code.');
    setLoading(false);
  };

  const handleReset = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    setLoading(true);
    const { error: resetError } = await confirmPasswordReset(email, code, newPassword);
    setLoading(false);
    if (resetError) {
      setError(resetError);
      return;
    }
    setStep('done');
  };

  const errorMessage = error ? (
    <div className="mb-6 flex gap-3 border-l-2 border-app-critical bg-app-surface px-4 py-3" role="alert">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-app-critical" aria-hidden="true" />
      <p className="text-sm leading-5 text-app-ink">{error}</p>
    </div>
  ) : null;

  const noticeMessage = !error && notice ? (
    <div className="mb-6 flex gap-3 border-l-2 border-app-positive bg-app-surface px-4 py-3" role="status">
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-app-positive" aria-hidden="true" />
      <p className="text-sm leading-5 text-app-ink">{notice}</p>
    </div>
  ) : null;

  const backToSignIn = (
    <p className="mt-8 border-t border-app-line pt-6 text-sm text-app-muted">
      <Link to="/login" className="inline-flex min-h-10 items-center gap-2 font-semibold text-app-ink underline decoration-app-line-strong underline-offset-4 hover:decoration-app-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to sign in
      </Link>
    </p>
  );

  if (step === 'done') {
    return (
      <AuthFrame eyebrow="Account security" title="Password updated" description="Your password has been changed. Sign in with your new password to continue.">
        <button type="button" onClick={() => navigate('/login')} className="btn-primary w-full">Go to sign in</button>
      </AuthFrame>
    );
  }

  if (step === 'reset') {
    return (
      <AuthFrame
        eyebrow="Account security"
        title="Choose a new password"
        description={destination
          ? `If an account exists for ${email}, we have sent a confirmation code to ${destination}. Enter it below with your new password.`
          : `If an account exists for ${email}, we have sent it a confirmation code. Enter it below with your new password.`}
      >
        {errorMessage}
        {noticeMessage}
        <form onSubmit={handleReset} className="space-y-5">
          <div>
            <label htmlFor="reset-code" className="mb-2 block text-sm font-medium text-app-ink">Confirmation code</label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
              <input id="reset-code" type="text" inputMode="numeric" value={code} onChange={event => setCode(event.target.value)} className="input-field !pl-11" placeholder="6-digit code from your email" autoComplete="one-time-code" autoFocus required />
            </div>
          </div>
          <div>
            <label htmlFor="reset-new-password" className="mb-2 block text-sm font-medium text-app-ink">New password</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
              <input id="reset-new-password" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={event => setNewPassword(event.target.value)} className="input-field !pl-11 !pr-11" placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`} autoComplete="new-password" required />
              <button type="button" onClick={() => setShowNewPassword(value => !value)} className="app-focus-ring absolute right-1 top-1/2 inline-flex min-h-10 min-w-10 -translate-y-1/2 items-center justify-center text-app-muted hover:text-app-ink" aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}>
                {showNewPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
              </button>
            </div>
            <p className="mt-2 text-xs leading-5 text-app-muted">{PASSWORD_POLICY_HINT}</p>
          </div>
          <div>
            <label htmlFor="reset-confirm-password" className="mb-2 block text-sm font-medium text-app-ink">Confirm password</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
              <input id="reset-confirm-password" type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} className="input-field !pl-11" placeholder="Repeat your new password" autoComplete="new-password" required />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full gap-2">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Updating password</> : 'Update password'}
          </button>
        </form>
        <p className="mt-6 flex flex-wrap items-center gap-x-2 text-sm text-app-muted">
          Did not receive it?
          <button type="button" onClick={handleResend} disabled={loading} className="app-focus-ring inline-flex min-h-10 items-center font-semibold text-app-ink underline decoration-app-line-strong underline-offset-4 hover:decoration-app-ink disabled:opacity-50">Send a new code</button>
        </p>
        {backToSignIn}
      </AuthFrame>
    );
  }

  return (
    <AuthFrame eyebrow="Account security" title="Reset your password" description="Enter the email address on your account and we will send you a confirmation code to set a new password.">
      {errorMessage}
      <form onSubmit={handleRequest} className="space-y-5">
        <div>
          <label htmlFor="reset-email" className="mb-2 block text-sm font-medium text-app-ink">Email address</label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
            <input id="reset-email" type="email" value={email} onChange={event => setEmail(event.target.value)} className="input-field !pl-11" placeholder="you@company.com" autoComplete="email" autoFocus required />
          </div>
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full gap-2">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Sending code</> : 'Send confirmation code'}
        </button>
      </form>
      {backToSignIn}
    </AuthFrame>
  );
}
